
import { useState } from "react";
import {
    controlePatientPointCardStyle,
    inputStyle,
    launchStatusInputStyle,
    patientLaunchMetaStyle,
    patientLaunchStatusCardStyle,
} from "./classicStyles";
import { formatDateTime } from "../shared/controleFormat";
import { CHARTER_COLORS as theme } from "../shared/controleTheme";
import { controlPointColor } from "../shared/controleVariants";
import { isPatientPointTouched } from "../shared/patientControlRules";
import { instructionInfoBadgeStyle, instructionTooltipStyle } from "../shared/styles/patientStyles";

export function PatientControlPointCard({ number, label, instruction, value, onChange }) {
    const hasBlockingIssue = isPatientPointTouched(value) && !value.validated;
    const [showInstruction, setShowInstruction] = useState(false);
    return (
        <div style={controlePatientPointCardStyle(value.validated, hasBlockingIssue)}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    borderRadius: "999px",
                    background: controlPointColor(number),
                    color: theme.ink,
                    fontSize: "12px",
                    fontWeight: 900,
                    flexShrink: 0,
                }}>
                    {number}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "7px", color: theme.ink, fontSize: "14px", fontWeight: 900, lineHeight: 1.35 }}>
                        <span>{label}</span>
                        {instruction && (
                            <span style={{ position: "relative", display: "inline-flex" }}>
                                <button
                                    type="button"
                                    aria-label="Afficher la marche à suivre"
                                    onMouseEnter={() => setShowInstruction(true)}
                                    onMouseLeave={() => setShowInstruction(false)}
                                    onFocus={() => setShowInstruction(true)}
                                    onBlur={() => setShowInstruction(false)}
                                    style={instructionInfoBadgeStyle}
                                >
                                    i
                                </button>
                                {showInstruction && (
                                    <span role="tooltip" style={instructionTooltipStyle}>
                                        {instruction}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    {hasBlockingIssue && (
                        <div style={{ color: theme.danger, fontSize: "12px", fontWeight: 800, marginTop: "4px" }}>
                            Point non validé, remonté dans le dossier de lot.
                        </div>
                    )}
                </div>
            </div>
            <div style={{ display: "grid", gap: "9px" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: theme.muted, fontSize: "13px", fontWeight: 800 }}>
                    <input
                        type="checkbox"
                        checked={value.validated}
                        onChange={event => onChange({ validated: event.target.checked })}
                        style={{ width: "18px", height: "18px", accentColor: theme.accent }}
                    />
                    <span>Point validé</span>
                </label>
                <label style={{ display: "grid", gap: "5px" }}>
                    <span style={{ color: theme.muted, fontSize: "12px", fontWeight: 800 }}>Commentaire (facultatif)</span>
                    <textarea
                        value={value.comment}
                        onChange={event => onChange({ comment: event.target.value })}
                        placeholder="Commentaire"
                        style={{ ...inputStyle, width: "100%", minHeight: "74px", resize: "vertical", lineHeight: 1.4 }}
                    />
                </label>
            </div>
        </div>
    );
}

export function PatientLaunchStatusCard({ value, onChange }) {
    const launchStatus = value.launchStatus || "PAS_LANCE";
    const launched = launchStatus === "LANCE";
    return (
        <div style={patientLaunchStatusCardStyle(launched)}>
            <div>
                <div style={{ color: theme.ink, fontSize: "14px", fontWeight: 950 }}>
                    Statut de lancement
                </div>
                <div style={{ marginTop: "4px", color: theme.muted, fontSize: "12px", fontWeight: 750 }}>
                    Point 2 · Traçage validation pharmaceutique
                </div>
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
                <label style={{ display: "grid", gap: "5px" }}>
                    <span style={{ color: theme.muted, fontSize: "12px", fontWeight: 850 }}>Statut</span>
                    <select
                        value={launchStatus}
                        onChange={event => onChange(event.target.value)}
                        style={launchStatusInputStyle(launchStatus)}
                    >
                        <option value="PAS_LANCE">Pas lancé</option>
                        <option value="LANCE">Lancé</option>
                    </select>
                </label>
                {launched && (
                    <div style={patientLaunchMetaStyle}>
                        <span>{formatDateTime(value.launchStatusAt) || "Date non renseignée"}</span>
                        <span>{value.launchStatusBy || "Opérateur non renseigné"}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export function ControlPointCard({ number, color, label, action }) {
    return (
        <div style={{
            background: "white",
            border: `1px solid ${theme.border}`,
            borderLeft: `6px solid ${color}`,
            borderRadius: "8px",
            padding: "11px 12px",
            boxShadow: "0 1px 2px rgba(12, 59, 60, 0.04)",
        }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", minWidth: 0 }}>
                    <span style={{
                        display: "inline-flex",
                        minWidth: "30px",
                        height: "26px",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "999px",
                        background: color,
                        color: theme.ink,
                        border: "1px solid rgba(12, 59, 60, 0.08)",
                        fontWeight: 900,
                        fontSize: "13px",
                        flexShrink: 0,
                    }}>
                        {number}
                    </span>
                    <div style={{ color: theme.ink, fontSize: "14px", fontWeight: 700, lineHeight: 1.35 }}>
                        {label}
                    </div>
                </div>
            </div>
            {action && (
                <div style={{
                    marginTop: "10px",
                    paddingTop: "10px",
                    borderTop: `1px solid ${theme.border}`,
                }}>
                    {action}
                </div>
            )}
        </div>
    );
}

export function PointTwoComment({ value, pointColor, onChange }) {
    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
            padding: "10px",
            borderRadius: "8px",
            background: theme.surfaceMuted,
            border: `1px solid ${pointColor}`,
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ color: theme.ink, fontSize: "13px", fontWeight: 900 }}>
                    Import des données via eureka:
                </span>
            </div>
            <textarea
                style={{
                    ...inputStyle,
                    width: "100%",
                    minHeight: "82px",
                    resize: "vertical",
                    lineHeight: 1.45,
                }}
                value={value}
                onChange={event => onChange(event.target.value)}
                placeholder="Commentaire"
            />
        </div>
    );
}

export function PointMultiComments({ fields, values, pointColor, onChange }) {
    return (
        <div style={{
            display: "grid",
            gap: "10px",
            width: "100%",
            padding: "10px",
            borderRadius: "8px",
            background: theme.surfaceMuted,
            border: `1px solid ${pointColor}`,
        }}>
            {fields.map(field => (
                <label key={field.key} style={{ display: "grid", gap: "6px" }}>
                    <span style={{ color: theme.ink, fontSize: "13px", fontWeight: 900 }}>
                        {field.title}
                    </span>
                    <textarea
                        style={{
                            ...inputStyle,
                            width: "100%",
                            minHeight: "76px",
                            resize: "vertical",
                            lineHeight: 1.45,
                        }}
                        value={values[field.key] || ""}
                        onChange={event => onChange(field.key, event.target.value)}
                        placeholder="Commentaire"
                    />
                </label>
            ))}
        </div>
    );
}
